import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { levelOfStructureService } from "@modules/organizationalStructure/levelOfStructure/services/levelOfStructure";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import LoadingButton from "@shared/components/LoadingButton";

export default function LevelOfStructureTranslation({ onClose, level }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { register, handleSubmit, formState: { errors }, reset, trigger, setValue, watch } = useForm();

    const watchedValues = watch();

    useEffect(() => {
        const fetchTranslations = async () => {
            if (level) {
                try {
                    setLoading(true);
                    setError(null);
                    
                    setValue('shortDescription', level.shortDescription || '');
                    setValue('longDescription', level.longDescription || '');
                    const translationData = await levelOfStructureService.getTranslation(level.id);
                    if (translationData && translationData.length > 0) {
                        const translation = translationData[0];
                        setValue('shortDescriptionTranslated', translation.shortDescription || '');
                        setValue('longDescriptionTranslated', translation.longDescription || '');
                    } else {
                        setValue('shortDescriptionTranslated', '');
                        setValue('longDescriptionTranslated', '');
                    }
                } catch (err) {
                    console.error('Error fetching translations:', err);
                    setError('Failed to load translations');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTranslations();
    }, [level, setValue]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const translationData = {
                orgUnitLevelsId: level.id,
                langCode: "en_GB",
                shortDescription: data.shortDescriptionTranslated,
                longDescription: data.longDescriptionTranslated,
                isTranslated: "Y"
            };

            await levelOfStructureService.saveTranslation(translationData);
            onClose();
            notifySuccess(t('common.translationSuccessfully'));
        } catch (error) {
            console.error('Error saving translation:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="modal-dialog modal-confirm" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-body">
                            <p>Loading translations...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('organizationalStructure.levelsOfStructuralGroups.translations.translations')}
                        </h4>
                        <form id="translationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.translations.abbreviation')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('shortDescription')}
                                            value={watchedValues.shortDescription || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.translations.name')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('longDescription')}
                                            value={watchedValues.longDescription || ''}
                                        />
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.translations.abbreviationTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('shortDescriptionTranslated')}
                                            value={watchedValues.shortDescriptionTranslated || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.translations.nameTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('longDescriptionTranslated')}
                                            value={watchedValues.longDescriptionTranslated || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="translationsForm" className="btn btn-primary" loading={loading} disabled={loading}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}